import { useState } from 'react';

import { ChevronDown, ChevronUp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { Collapse } from 'react-collapse';

import type { useCart } from 'src/api/queries';
import { Button, Divider, Skeleton } from 'src/components';
import { formatAmount } from 'src/utils/productUtils';

import { Trans, useLingui } from '@lingui/react/macro';
import { cartHelpers } from 'src/utils/cartHelpers';
import { useFreeTrialLengthText } from 'src/utils/freeTrialLength';
import { CartItemConnectWithTrial } from './CartItemConnectWithTrial';
import { Price } from './Price';

export function CartDetailsOffer({
  cart,
}: {
  cart: ReturnType<typeof useCart>;
}) {
  const { t, i18n } = useLingui();
  const [showDetails, setShowDetails] = useState(false);
  const updatingPrice =
    cart.updateCurrency.isPending ||
    cart.updateTax.isPending ||
    cart.setup.isPending;

  const freeTrialLengthDays = cart.setup.data?.freeTrialLengthDays;
  const freeTrialLengthText = useFreeTrialLengthText(freeTrialLengthDays ?? 0);
  const freeTrialLengthDisplay = freeTrialLengthDays
    ? freeTrialLengthText
    : t`Free trial`;

  const billingStartsDate = cartHelpers.formatBillingStartDate(
    cart.details.data,
    i18n.locale
  );

  return (
    <div>
      <div className="flex w-full flex-col bg-neutral-light-3 p-8 ll:hidden">
        <Button
          variant="tertiary-neutral"
          className="w-full bg-none"
          onClick={() => setShowDetails((showDetails) => !showDetails)}
          data-cy="cookie-banner-show-details"
        >
          <span className="flex w-full items-center justify-between">
            <span className="mr-auto">
              <Skeleton isLoading={cart.setup.isPending}>
                {freeTrialLengthDisplay}
              </Skeleton>
            </span>
            <span>
              <Skeleton isLoading={updatingPrice}>
                <b data-cy="connect-plan-row-price">
                  {formatAmount(
                    0,
                    cart.details.data?.data.currency,
                    i18n.locale
                  )}
                </b>
              </Skeleton>
            </span>
            {showDetails ? (
              <ChevronUp className="ml-16 inline max-w-20" />
            ) : (
              <ChevronDown className="ml-16 inline max-w-20" />
            )}
          </span>
        </Button>
        <Collapse isOpened={showDetails}>
          <Divider className="my-16" />
          <div className="flex flex-col bg-neutral-light-3 px-16 pb-16">
            <div className="flex justify-between" data-cy="billing-starts-row">
              <p data-cy="billing-start-date" className="my-4">
                <Trans>Billing starts&nbsp;{billingStartsDate}</Trans>
              </p>
              <p className="my-4">
                <Skeleton isLoading={updatingPrice}>
                  <Price cart={cart} showTaxStatus />
                </Skeleton>
              </p>
            </div>
            <Typography variant="body-sm" className="mt-16">
              <Trans>
                We’ll send you a reminder email before your free trial ends.
              </Trans>
            </Typography>
          </div>
        </Collapse>
      </div>

      <div className="hidden flex-col bg-neutral-light-3 p-20 ll:flex">
        <CartItemConnectWithTrial cart={cart} />
      </div>
    </div>
  );
}
